<template>
  <div class="uncertainty-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>位置分布 (Δx)</label>
            <span class="val">{{ deltaX.toFixed(2) }} <small>pm</small></span>
          </div>
          <div class="hud-item orange">
            <label>动量弥散 (Δp)</label>
            <span class="val">{{ deltaP.toFixed(2) }} <small>kg·m/s</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <radialGradient id="electron-cloud" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stop-color="#38bdf8" stop-opacity="0.8" />
                <stop offset="60%" stop-color="#38bdf8" stop-opacity="0.2" />
                <stop offset="100%" stop-color="#38bdf8" stop-opacity="0" />
             </radialGradient>
             <filter id="glow-quantum"><feGaussianBlur stdDeviation="4" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 坐标轴 -->
          <line x1="40" y1="120" x2="360" y2="120" stroke="#1e293b" stroke-width="2" stroke-dasharray="4,4" />
          
          <!-- 量子概率云 (位置) -->
          <ellipse cx="200" cy="120" :rx="deltaX * 4" :ry="Math.max(10, 50 - deltaP*0.3)" fill="url(#electron-cloud)" filter="url(#glow-quantum)">
             <animate attributeName="opacity" values="0.6;1;0.6" dur="0.8s" repeatCount="indefinite" />
          </ellipse>

          <!-- 动量特征点 (随机扰动展示 Δp) -->
          <g v-for="n in 30" :key="n">
             <circle :cx="200 + getRandPos(n).x" :cy="120 + getRandPos(n).y" r="1.5" fill="#fff" :opacity="getRandPos(n).opacity">
                <animate attributeName="cx" :values="`${200 + getRandPos(n).x};${200 + getRandPos(n).x + (Math.random()-0.5)*deltaP};${200 + getRandPos(n).x}`" dur="0.1s" repeatCount="indefinite" />
             </circle>
          </g>

          <!-- 能量级指示 -->
          <text x="350" y="110" text-anchor="middle" fill="#475569" font-size="10" font-weight="bold">x</text>
        </svg>

        <div class="interaction-hint">利用滑块压缩位置 Δx 观察动量 Δp 的剧烈反弹</div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-col gap-2">
              <div class="flex items-center justify-between">
                 <span class="label">约束粒子位置空间 (Δx)</span>
                 <span class="val-tag">{{ deltaX < 10 ? '极度受限' : '较宽松' }}</span>
              </div>
              <el-slider v-model="deltaX" :min="2" :max="40" :step="0.1" size="small"></el-slider>
              
              <div class="quantum-meter mt-1">
                 <div class="meter-fill" :style="{ width: (deltaP / 25) * 100 + '%', background: deltaP > 15 ? '#ef4444' : '#38bdf8' }"></div>
                 <span class="meter-label">动量不确定度 Δp 指示</span>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>这并非测量技术的限制，而是物质的<strong>波粒二象性本质</strong>。粒子的空间定位Δx越准确，其波包包含的谐波成分就越杂，导致动量Δp范围越广。<strong>位置与动量的乘积存在下限</strong>，这是量子力学最根本的法则之一。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UncertaintyViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      deltaX: 20
    }
  },
  computed: {
    deltaP() {
       // Constants for visualization: deltaX * deltaP = constant
       return 50 / (this.deltaX || 1);
    }
  },
  methods: {
    getRandPos(n) {
       // Semi-random points constrained by deltaX and deltaP logic
       const seed = Math.sin(n * 123.45);
       return {
          x: (seed * this.deltaX * 3.5),
          y: (Math.cos(n * 456.78) * 20),
          opacity: 0.2 + Math.abs(seed) * 0.6
       };
    }
  }
}
</script>

<style scoped lang="scss">
.uncertainty-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #f97316; }
    }
  }
  .interaction-hint { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 10px; font-weight: bold; }
}

.quantum-meter {
   height: 6px; background: #1e293b; border-radius: 3px; position: relative; margin-top: 5px;
   .meter-fill { height: 100%; border-radius: 3px; transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
   .meter-label { position: absolute; right: 0; top: -12px; font-size: 9px; color: #64748b; font-weight: bold; }
}

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-col { flex-direction: column; } .flex-1 { flex: 1; } .gap-2 { gap: 8px; } .justify-between { justify-content: space-between; } .items-center { align-items: center; }
.val-tag { color: #475569; font-size: 11px; font-weight: 700; }

.discovery-card-compact {
  background: #eff6ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #dbeafe; display: flex; align-items: center; gap: 10px;
  i { color: #3b82f6; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #1e3a8a; line-height: 1.5; strong { color: #1e40af; } }
}
</style>
